// ChatInterface.js
"use client";
import React, { useState, useEffect } from "react";
import chatService from "@/service/chat.service";
import { useSelector } from "react-redux";
import InputArea from "../InputArea";
import ChatHeader from "../ChatHeader/ChatHeader";
import MessagesContainer from "../MessagesContainer";
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import ToastService from "@/service/toaster.service";
import { Box, useMediaQuery, useTheme } from "@mui/material";


function ChatInterface({ conversationId, role, receiverId, chatUser, twoFactorEnabled = false, userRole }) {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [otherUserTyping, setOtherUserTyping] = useState(false);

  const customerData = useSelector((state) => state.auth.auth);


  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const response = await chatService.getConversations(conversationId, role);
        const fetchedMessages = response.data.messages || [];
        
        const formattedMessages = fetchedMessages.map((msg) => ({
          id: msg._id,
          senderRole: msg.senderType,
          message: msg.body == "file" ? null : msg.body,  
          file: msg.file,
          time: new Date(msg.createdAt).toLocaleTimeString(),
          senderId: msg.sender,
          sender: msg.sender,
          createdAt: msg.createdAt,
        }));
        setMessages(formattedMessages);
      } catch (error) {
        ToastService.error("Failed to fetch conversations");
      } finally {
        setLoading(false);
      }
    };

    chatService.connect();

    chatService.socket.on("message", (msg) => {
      const { message } = msg;
      if (!message) {
        console.error("Invalid message format:", msg);
        return;
      }

     if(message.sender === receiverId) {
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            id: message._id,
            senderRole: message.senderType,
            message: message.body,
            file: message.file,
            time: new Date(message.createdAt).toLocaleTimeString(),
            senderId: message.senderId,
            sender: chatUser,
            createdAt: msg.createdAt || new Date().toISOString(),
          },
        ]);
      }
    });

    chatService.socket.on("typing", (data) => {
      if (data.senderId === receiverId) {
        setOtherUserTyping(data.isTyping);
      }
    });

    chatService.socket.on("fileUploaded", (fileMessages) => {
      
      // In case it's not an array, convert it to one
      const messagesArray = Array.isArray(fileMessages) ? fileMessages : [fileMessages];
      const validMessages = messagesArray.filter(msg => msg.sender === receiverId);
    
      if (validMessages.length > 0) {
        setMessages((prevMessages) => [
          ...prevMessages,
          ...validMessages.map((fileMessage) => ({
            id: fileMessage._id,
            senderRole: fileMessage.senderType,
            message: fileMessage.body === "file" ? null : fileMessage.body,
            file: {
              file_url: fileMessage.file.file_url,
              original_name: fileMessage.file.original_name,
              file_type: fileMessage.file.file_type,
            },
            time: new Date(fileMessage.createdAt).toLocaleTimeString(),
            senderId: fileMessage.sender,
            sender: chatUser,
            createdAt: fileMessage.createdAt || new Date().toISOString(),
          })),
        ]);
      }
    });
    
    fetchConversations();
    return () => {
      chatService.disconnect();
    };
  }, [conversationId]);

  useEffect(() => {
    const enableAudio = () => {
      window.userInteracted = true;
      window.removeEventListener("click", enableAudio);
    };
    window.addEventListener("click", enableAudio);
  }, []);

  
  const handleSend = async () => {
    if (message.trim() || files.length > 0) {
      const formData = new FormData();
      formData.append("message", message !== '' ? message : "file");
      formData.append("senderType", role);
      formData.append("conversationId", conversationId);
      formData.append("receiverId", receiverId);
      formData.append("role", role);
      files.forEach((file) => formData.append("files", file));

      try {
        setLoading(true);

        if (files.length > 0) {
          const response = await chatService.uploadFile(formData);
          const uploadedFiles = response.data;
          const uploadedFileUrls = uploadedFiles.map((file) => file.file.file_url);
          setFilePreviews(uploadedFileUrls);

          uploadedFiles.forEach((uploadedFile) => {
            const newMessage = {
              id: uploadedFile._id,
              senderRole: role,
              message:null,
              file: {
                file_url: uploadedFile.file.file_url,
                original_name: uploadedFile.file.original_name,
                file_type: uploadedFile.file.file_type,
                s3_url_path: uploadedFile.file.s3_url_path,
              },
              time: new Date().toLocaleTimeString(),
              senderId: receiverId,
              sender: customerData,
              createdAt: uploadedFile.createdAt || new Date().toISOString(),

            };
            setMessages((prevMessages) => [...prevMessages, newMessage]);
          });
          setMessage("");
          setFiles([]);
          setFilePreviews([]);
        } else {
          const response = await chatService.sendMessage({
            message: message,
            senderType: role,
            conversationId,
            receiverId,
          });
          setMessages((prevMessages) => [
            ...prevMessages,
            {
              id: new Date().toLocaleTimeString(),
              senderRole: role,
              message: message,
              file: null,
              time: new Date().toLocaleTimeString(),
              senderId: receiverId,
              sender:customerData,
              createdAt: new Date().toISOString(),

            },
          ]);
          setMessage("");
          setFiles([]);
          setFilePreviews([]);
        }
      } catch (error) {
        console.error("Failed to send message or upload file:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const maxSize = 25 * 1024 * 1024; // 5 MB in bytes
  
    const validFiles = selectedFiles.filter(file => file.size <= maxSize);
    const invalidFiles = selectedFiles.filter(file => file.size > maxSize);
  
    if (invalidFiles.length > 0) {
      // Display an error message for the invalid files
      ToastService.error("Some files exceed the maximum size of 25 MB.");
    }
  
    setFiles(validFiles);
  
    const previews = validFiles.map((file) => {
      const previewUrl = URL.createObjectURL(file);
      return {
        type: file.type,
        preview: previewUrl,
        name: file.name,
      };
    });
    e.target.value = '';
    setFilePreviews(previews);
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    const updatedPreviews = filePreviews.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    setFilePreviews(updatedPreviews);
  };


  const handleTyping = () => {
    if (!isTyping) {
      setIsTyping(true);
      chatService.socket.emit("typing", { receiverId, isTyping: true });
    }

    clearTimeout(typingTimeout);
    const newTimeout = setTimeout(() => {
      setIsTyping(false);
      chatService.socket.emit("typing", { receiverId, isTyping: false });
    }, 3000);

    setTypingTimeout(newTimeout);
  };
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box
      component="section"
      sx={{
        bgcolor: "#fff",
        border: { xs: "none", md: "1px solid #e0e0e0" },
        borderRadius: { xs: "12px", md: "16px 0 0 0" },
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        alignItems: "stretch",
        width: "100%",
        height: "100%",
        boxShadow: { 
          xs: "0 4px 20px rgba(0, 0, 0, 0.08)", 
          md: "14px 28px 45px #7090b033" 
        },
        overflow: "hidden",
        position: "relative",
        // Mobile optimizations
        mt: { xs: 1, md: 0 },
        mx: { xs: 0.5, md: 0 },
        maxWidth: { xs: "100%", md: "none" },
        // Enhanced visual design
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          // background: 'linear-gradient(90deg, #5e5acf, #2196f3, #5e5acf)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 3s ease-in-out infinite',
          borderRadius: '12px 12px 0 0',
        },
        '@keyframes shimmer': {
          '0%': { backgroundPosition: '-200% 0' },
          '100%': { backgroundPosition: '200% 0' }
        }
      }}
    >
      <ToastContainer />
      <ChatHeader conversationId={conversationId} chatUser={chatUser} />
      <MessagesContainer messages={messages} role={role} chatUser={chatUser} otherUserTyping={otherUserTyping} twoFactorEnabled={twoFactorEnabled} userRole={userRole} />
      <InputArea
        message={message}
        setMessage={setMessage}
        handleSend={handleSend}
        handleFileChange={handleFileChange}
        handleRemoveFile={handleRemoveFile}
        filePreviews={filePreviews}
        loading={loading}
        onKeyPress={(e) => {
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleSend();
          }
        }}
        onChange={(e) => {
          setMessage(e.target.value);
          handleTyping();
        }}
      />
    </Box>
  );
}

export default ChatInterface;